<template>
  <div class="profile-card">
    <div class="profile-avatar">
      <div class="profile-avatar__image">
        <img :src="getAvatarUrl()" alt="" v-if="getAvatarUrl()" />
        <div v-else>
          <img src="@/components/profile/assets/camera.svg" alt="" />
        </div>
      </div>
      <div
        class="profile-avatar__button"
        @click="handleShowForm('addPhoto', avatar)"
      >
        <img src="@/components/profile/assets/edit_green.svg" alt="" />
      </div>
    </div>
    <div class="profile-card__info">
      <a-dropdown
        class="edit-profile-dropdown"
        :trigger="['click']"
        v-model:open="isVisibleDropdown"
        placement="bottomRight"
        destroyPopupOnHide
      >
        <div class="edit-profile-dropdown__icon">
          <img src="@/components/profile/assets/more_vertical.svg" alt="" />
        </div>
        <template #overlay>
          <a-menu class="edit-profile-dropdown__menu">
            <a-menu-item class="edit-profile-dropdown__menu-item">
              <a @click.stop="handleShowForm('editProfile')">
                <img src="@/components/profile/assets/edit_black.svg" alt="" />
                <span>Редактировать</span>
              </a>
            </a-menu-item>
            <a-menu-item class="edit-profile-dropdown__menu-item" disabled>
              <a @click.stop="() => {}">
                <img
                  class="csv"
                  src="@/components/profile/assets/file_blank_gray.svg"
                  alt=""
                />
                <span>Выгрузить CV</span>
              </a>
            </a-menu-item>
            <a-menu-item class="edit-profile-dropdown__menu-item">
              <a-switch
                size="small"
                v-model:checked="isVisibleToPartner"
                :loading="isLoadingVisibleToPartner"
                @change="changePartnerVisibility"
              />
              <span>Видно партнеру</span>
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>
      <div class="profile-card-portfolio" v-if="person?.portfolioUrl">
        <h4>Мое портфолио</h4>
        <a
          :href="person.portfolioUrl"
          target="_blank"
          rel="noopener noreferrer"
          >{{ person.portfolioUrl }}</a
        >
      </div>
      <div class="profile-card-description">
        <h4>Связаться со мной</h4>
        <div class="profile-card-description__item">
          <div class="profile-card-description__label">E-mail:</div>
          <div class="profile-card-description__content">
            {{ person.email }}
          </div>
        </div>
        <div class="profile-card-description__item">
          <div class="profile-card-description__label">Телефон:</div>
          <div class="profile-card-description__content">
            {{ person.phone }}
          </div>
        </div>
        <div class="profile-card__social-icons">
          <SocialIcons :person="person" />
        </div>
      </div>
    </div>
  </div>
  <c-drawer v-model:open="showForm" destroyOnClose>
    <component
      :is="form"
      v-bind:item="formItem"
      @close="() => (showForm = !showForm)"
      @update="() => emit('update')"
    />
  </c-drawer>
</template>

<script setup>
  import { shallowRef, ref, onMounted, onBeforeUnmount, watch } from 'vue';
  import EditProfileForm from '@/components/profile/EditProfileForm';
  import AddPhotoForm from '@/components/profile/AddPhotoForm';
  import CDrawer from '@/components/UI/CDrawer.vue';
  import SocialIcons from '@/components/social/SocialIcons.vue';
  import { debounce } from 'lodash-es';
  import { useApi } from '@/api/use-api';

  const props = defineProps({
    person: Object,
    avatar: Object,
  });

  const emit = defineEmits(['update']);
  const api = useApi();
  const form = shallowRef(null);
  const formItem = ref(null);
  const showForm = ref(false);
  const isVisibleDropdown = ref(false);
  const isVisibleToPartner = ref(false);
  const isLoadingVisibleToPartner = ref(false);

  function handleShowForm(formType, item) {
    switch (formType) {
      case 'addPhoto':
        form.value = AddPhotoForm;
        formItem.value = item;
        break;

      case 'editProfile':
        isVisibleDropdown.value = false;
        form.value = EditProfileForm;
        break;
    }

    showForm.value = true;
  }

  onMounted(() => {
    isVisibleToPartner.value = props.person.isVisibleToPartner;
    window.addEventListener('scroll', handleScroll, { capture: true });
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
  });

  watch(
    () => props.person.isVisibleToPartner,
    () => {
      isVisibleToPartner.value = props.person.isVisibleToPartner;
    }
  );

  // Close Edit Profile dropdown menu on scroll
  const handleScroll = debounce(() => {
    if (isVisibleDropdown.value) {
      isVisibleDropdown.value = false;
    }
  }, 50);

  function getAvatarUrl() {
    return props.avatar?.attachmentUid
      ? '/api/v1/attachment/' + props.avatar.attachmentUid
      : null;
  }

  const changePartnerVisibility = async () => {
    try {
      isLoadingVisibleToPartner.value = true;
      const response = await api.me.changePartnerVisibility();
      isVisibleToPartner.value = response.isVisibleToPartner;
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoadingVisibleToPartner.value = false;
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors.scss' as *;

  .profile-avatar {
    position: relative;

    &__image {
      position: relative;
      width: 325px;
      height: 280px;
      overflow: hidden;

      & img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__button {
      border-radius: 8px;
      background-color: $color-bg-primary-white;
      box-shadow: 0 2px 5.5px 0 rgba(0, 0, 0, 0.06);
      cursor: pointer;
      height: 55px;
      width: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: 0;
      bottom: 0;

      &:hover {
        background-color: $color-btn-special-hover;

        img {
          filter: sepia(100%) brightness(200%);
        }
      }

      img {
        width: 19px;
        height: 19px;
      }
    }
  }

  .profile-card {
    background-color: #f2f4f8;
    border-radius: 4px;
    overflow: hidden;
    width: 325px;
    height: fit-content;
    display: flex;
    flex-direction: column;
    margin: 0 20px 30px 0;

    &__info {
      padding: 20px;
      width: 100%;
      height: auto;

      & a {
        color: #007bff;
        text-decoration: none;
      }

      & a:hover {
        text-decoration: underline;
      }

      &__social-icons {
        margin-top: 20px;
        display: flex;
        justify-content: flex-start;
        gap: 10px;

        & img {
          width: 24px;
          height: 24px;
          cursor: pointer;
          transition: transform 0.2s;

          &:hover {
            transform: scale(1.2);
          }
        }
      }
    }
  }

  .edit-profile-dropdown__icon {
    background-color: $color-bg-primary-white;
    border-radius: 5px;
    cursor: pointer;
    height: 30px;
    padding: 3px;
    position: absolute;
    right: 40px;
    width: 30px;
  }

  .edit-profile-dropdown__menu {
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 20px 10px;
    z-index: auto;

    .edit-profile-dropdown__menu-item {
      span {
        font: 700 16px/26px 'Nunito Sans';
        color: $color-text-secondary-1;
        margin-left: 14px;
      }

      button {
        background: $color-btn-primary-disabled;

        &:hover {
          background-color: $color-btn-primary-disabled;
        }
      }
      button.ant-switch-checked {
        background: $color-btn-primary;

        &:hover {
          background-color: $color-btn-special-hover;
        }
      }
    }

    .edit-profile-dropdown__menu-item.ant-dropdown-menu-item-disabled {
      span:last-child {
        color: $color-text-secondary-grey;
      }
    }
  }

  .profile-card-portfolio {
    margin-bottom: 20px;
  }

  .profile-card-description {
    &__label {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-right: 10px;
    }

    &__item {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-bottom: 10px;
    }

    &__content {
      font-size: 14px;
      color: #666;
    }
  }

  h4 {
    margin-bottom: 10px;
    font-size: 18px;
    font-weight: bold;
    color: #333;
  }
</style>
